import { apiClient } from '@/services/api-client';
import type {
  ProviderCheckoutLink,
  ProviderCheckoutLinkCreateInput,
  ProviderCheckoutLinkCreateResult,
  ProviderCheckoutLinkListResponse,
  ProviderCheckoutLinkStatus,
  ProviderCheckoutLinkOptions,
} from '@/types/provider-checkout-links';

type ApiPayload = {
  data?: unknown;
  message?: string;
  errors?: Record<string, string[]>;
};

const readString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;
const readNullableString = (value: unknown): string | null =>
  typeof value === 'string' ? value : null;
const readNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const normalizeStatus = (value: unknown): ProviderCheckoutLinkStatus => {
  if (value === 'active' || value === 'expired' || value === 'consumed') {
    return value;
  }
  return 'active';
};

const normalizeLink = (raw: Record<string, unknown>): ProviderCheckoutLink => {
  const record = (raw.attributes && typeof raw.attributes === 'object'
    ? raw.attributes
    : raw) as Record<string, unknown>;

  return {
    id: readString(raw.id ?? record.id) ?? '',
    productId: readString(record.productId ?? record.product_id) ?? '',
    planId: readNullableString(record.planId ?? record.plan_id),
    packageId: readNullableString(record.packageId ?? record.package_id),
    cycleId: readNullableString(record.cycleId ?? record.cycle_id),
    priceId: readNullableString(record.priceId ?? record.price_id),
    currency: readNullableString(record.currency),
    status: normalizeStatus(record.status),
    createdAt: readString(record.createdAt ?? record.created_at) ?? '',
    expiresAt: readNullableString(record.expiresAt ?? record.expires_at),
    usedAt: readNullableString(record.usedAt ?? record.used_at),
  };
};

const normalizeOptions = (payload: Record<string, unknown> | undefined): ProviderCheckoutLinkOptions => {
  const data = (payload?.data ?? payload) as Record<string, unknown> | undefined;
  const readArray = (value: unknown) => (Array.isArray(value) ? value : []);

  const products = readArray(data?.products)
    .filter((entry): entry is Record<string, unknown> => Boolean(entry && typeof entry === 'object'))
    .map((entry) => ({
      id: readString(entry.id) ?? '',
      name: readString(entry.name) ?? '',
    }))
    .filter((entry) => entry.id && entry.name);

  const plans = readArray(data?.plans)
    .filter((entry): entry is Record<string, unknown> => Boolean(entry && typeof entry === 'object'))
    .map((entry) => ({
      id: readString(entry.id) ?? '',
      name: readString(entry.name) ?? '',
      productId: readString(entry.productId ?? entry.product_id) ?? '',
      isVariant: Boolean(entry.isVariant ?? entry.is_variant),
      parentPlanId: readNullableString(entry.parentPlanId ?? entry.parent_plan_id),
    }))
    .filter((entry) => entry.id && entry.name && entry.productId);

  const packages = readArray(data?.packages)
    .filter((entry): entry is Record<string, unknown> => Boolean(entry && typeof entry === 'object'))
    .map((entry) => ({
      id: readString(entry.id) ?? '',
      name: readString(entry.name) ?? '',
      productId: readString(entry.productId ?? entry.product_id) ?? '',
    }))
    .filter((entry) => entry.id && entry.name && entry.productId);

  const prices = readArray(data?.prices)
    .filter((entry): entry is Record<string, unknown> => Boolean(entry && typeof entry === 'object'))
    .map((entry) => ({
      id: readString(entry.id) ?? '',
      planId: readNullableString(entry.planId ?? entry.plan_id),
      packageId: readNullableString(entry.packageId ?? entry.package_id),
      cycleId: readNullableString(entry.cycleId ?? entry.cycle_id),
      currency: readString(entry.currency) ?? 'USD',
      amount: readNumber(entry.amount),
    }))
    .filter((entry) => entry.id && (entry.planId || entry.packageId));

  const cycles = readArray(data?.cycles)
    .filter((entry): entry is Record<string, unknown> => Boolean(entry && typeof entry === 'object'))
    .map((entry) => ({
      id: readString(entry.id) ?? '',
      productId: readNullableString(entry.productId ?? entry.product_id),
      name: readString(entry.name) ?? '',
      months: readNumber(entry.months),
    }))
    .filter((entry) => entry.id && entry.name);

  return {
    products,
    plans,
    packages,
    prices,
    cycles,
  };
};

export class ProviderCheckoutLinkRequestError extends Error {
  status?: number;
  payload?: unknown;
  errors?: Record<string, string[]>;

  constructor(message: string, status?: number, payload?: unknown) {
    super(message);
    this.status = status;
    this.payload = payload;
    if (payload && typeof payload === 'object' && 'errors' in (payload as Record<string, unknown>)) {
      this.errors = (payload as { errors?: Record<string, string[]> }).errors;
    }
  }
}

class CustomerCheckoutLinksService {
  async list(customerId: string): Promise<ProviderCheckoutLinkListResponse> {
    try {
      const response = await apiClient.get<ApiPayload>(`/api/v1/customers/${customerId}/checkout-links`);
      const payload = (response.data?.data ?? response.data) as Record<string, unknown> | undefined;
      const rawLinks = Array.isArray(payload?.checkout_links)
        ? payload?.checkout_links
        : Array.isArray(payload?.checkoutLinks)
          ? payload?.checkoutLinks
          : [];
      const checkoutLinks = rawLinks
        .filter((entry): entry is Record<string, unknown> => Boolean(entry && typeof entry === 'object'))
        .map(normalizeLink)
        .filter((link) => link.id);

      return { checkoutLinks };
    } catch (error: unknown) {
      const response = (error as { response?: { status?: number; data?: unknown } })?.response;
      if (response) {
        throw new ProviderCheckoutLinkRequestError(
          (response.data as { message?: string } | undefined)?.message ?? 'Unable to load checkout links.',
          response.status,
          response.data,
        );
      }
      throw new ProviderCheckoutLinkRequestError('Unable to load checkout links.', undefined, error);
    }
  }

  async options(
    customerId: string,
    checkoutType?: 'subscription' | 'package',
  ): Promise<ProviderCheckoutLinkOptions> {
    try {
      const query = checkoutType ? `?checkout_type=${encodeURIComponent(checkoutType)}` : '';
      const response = await apiClient.get<ApiPayload>(
        `/api/v1/customers/${customerId}/checkout-link-options${query}`,
      );
      return normalizeOptions((response.data ?? {}) as Record<string, unknown>);
    } catch (error: unknown) {
      const response = (error as { response?: { status?: number; data?: unknown } })?.response;
      if (response) {
        throw new ProviderCheckoutLinkRequestError(
          (response.data as { message?: string } | undefined)?.message ?? 'Unable to load checkout link options.',
          response.status,
          response.data,
        );
      }
      throw new ProviderCheckoutLinkRequestError('Unable to load checkout link options.', undefined, error);
    }
  }

  async create(
    customerId: string,
    input: ProviderCheckoutLinkCreateInput,
  ): Promise<ProviderCheckoutLinkCreateResult> {
    const body: Record<string, unknown> = {
      product_id: input.productId,
    };

    if (input.packageId) {
      body.package_id = input.packageId;
    } else if (input.planId) {
      body.plan_id = input.planId;
    }

    if (input.cycleId) {
      body.cycle_id = input.cycleId;
    }

    if (input.priceId) {
      body.price_id = input.priceId;
    }

    if (input.currency) {
      body.currency = input.currency;
    }

    if (input.expiresAt) {
      body.expires_at = input.expiresAt;
    }

    try {
      const response = await apiClient.post<ApiPayload>(`/api/v1/customers/${customerId}/checkout-link`, body);
      const payload = (response.data?.data ?? response.data) as Record<string, unknown> | undefined;
      return {
        checkoutUrl: readString(payload?.checkout_url ?? payload?.checkoutUrl) ?? '',
        token: readString(payload?.token) ?? '',
        expiresAt: readNullableString(payload?.expires_at ?? payload?.expiresAt),
      };
    } catch (error: unknown) {
      const response = (error as { response?: { status?: number; data?: unknown } })?.response;
      if (response) {
        throw new ProviderCheckoutLinkRequestError(
          (response.data as { message?: string } | undefined)?.message ?? 'Unable to generate checkout link.',
          response.status,
          response.data,
        );
      }
      throw new ProviderCheckoutLinkRequestError('Unable to generate checkout link.', undefined, error);
    }
  }
}

export const customerCheckoutLinksService = new CustomerCheckoutLinksService();
